import React from 'react';
import cx from 'classnames';

import Box from '../../Box';
import { IconProps } from '../Icon';
import Text, { TextProps } from '../../Text';
import Touchable, { Props as TouchableProps } from '../../Touchable';

// ToDo: Investigate Dynamic import
import ChevronLeft14 from '../Icon/library/ChevronLeft14';
import ChevronLeft16 from '../Icon/library/ChevronLeft16';
import ChevronLeft18 from '../Icon/library/ChevronLeft18';
import ChevronLeft20 from '../Icon/library/ChevronLeft20';

import ChevronRight14 from '../Icon/library/ChevronRight14';
import ChevronRight16 from '../Icon/library/ChevronRight16';
import ChevronRight18 from '../Icon/library/ChevronRight18';
import ChevronRight20 from '../Icon/library/ChevronRight20';

import styles from './Link.module.scss';

export interface LinkProps
  extends Omit<
    TouchableProps,
    | 'size'
    | 'className'
    | 'block'
    | 'componentProps'
    | 'inline'
    | 'wide'
    | 'type'
  > {
  /** The content for `Link`. */
  children?: React.ReactNode;
  /** Apply CSS className to container. */
  className?: Parameters<typeof cx>[0];
  /**
   * Escape hatch to add props to internal elements.
   */
  components?: {
    Text: TextProps;
  };
  /**
   * Specify the `Button`'s width.
   * @default block
   */
  display?: 'inline' | 'block';
  /**
   * When `true`, sets the component to the disable state and prevents any interaction.
   * @default false
   */
  disabled?: boolean;
  /**
   * Specify the icon that will appear before the text.
   * @default chevron-left
   */
  icon?: React.ReactElement<IconProps> | null;
  /** Specific the icon placement.
   * @default left
   */
  iconPlacement?: 'left' | 'right';
  /**
   * Specify the font size within the `Button`. (Inherited from Text component)
   * @default 16
   */
  size?: 12 | 14 | 16 | 18 | 20;
}

const ChevronLeftDictionary = {
  12: <ChevronLeft14 />,
  14: <ChevronLeft16 />,
  16: <ChevronLeft18 />,
  18: <ChevronLeft18 />,
  20: <ChevronLeft20 />,
};
const ChevronRightDictionary = {
  12: <ChevronRight14 />,
  14: <ChevronRight16 />,
  16: <ChevronRight18 />,
  18: <ChevronRight18 />,
  20: <ChevronRight20 />,
};

export const Link = ({
  children,
  className,
  components,
  display = 'block',
  icon,
  iconPlacement = 'left',
  size = 16,
  disabled,
  ...restProps
}: LinkProps) => {
  const hasNoIcon = icon === null;
  const hasCustomIcon = icon !== undefined;
  const hasIconPlacementRight = iconPlacement === 'right';
  const isInline = display === 'inline';

  return (
    <Touchable
      className={cx(
        styles.link,
        disabled && styles.isDisabled,
        isInline && styles.isInline,
        className,
      )}
      disabled={disabled}
      {...restProps}
    >
      <Box display={isInline ? 'inline' : 'flex'} alignItems='center' gap={0.5}>
        {!hasIconPlacementRight && !isInline && (
          <Box
            as='span'
            className={cx(
              styles.iconContainer,
              !hasIconPlacementRight && styles.iconPlacementLeft,
              hasCustomIcon && styles.hasCustomIcon,
            )}
          >
            {!isInline &&
              !hasNoIcon &&
              React.cloneElement(
                hasCustomIcon ? icon : ChevronLeftDictionary[size],
                {
                  className: cx(styles.icon),
                },
              )}
          </Box>
        )}
        <Text
          weight='semiBold'
          className={styles.text}
          as='span'
          size={size}
          {...components?.Text}
        >
          {children}
        </Text>
        {hasIconPlacementRight && !isInline && (
          <Box
            className={cx(
              styles.iconContainer,
              hasIconPlacementRight && styles.iconPlacementRight,
              hasCustomIcon && styles.hasCustomIcon,
            )}
          >
            {!isInline &&
              !hasNoIcon &&
              React.cloneElement(
                hasCustomIcon ? icon : ChevronRightDictionary[size],
                {
                  className: cx(styles.icon),
                },
              )}
          </Box>
        )}
      </Box>
    </Touchable>
  );
};

Link.displayName = 'Link';
export default Link;
export { styles };
